import { useCallback, useEffect, useMemo } from "react";
import ScreenCard from "../screens/ScreenCard";
import MarkdownView from "../components/MarkdownView";
import { useI18n } from "../hooks/i18n-hooks";
import { StyleSheet } from "react-native";
import { useTheme } from "../hooks/use-theme";
import ScreenTitle from "../components/ScreenTitle";
import Text from "./Text";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";
import LicorneColourSvg from "../assets/images/licorne-colour.svg";
import Gap from "../ui_atoms/Gap";
import { useIntercom } from "../hooks/intercom";

type ErrorComponentProps = {
    onClose: () => void;
    error: Error;
    isNotFound?: boolean;
};

export const ErrorComponent = ({ onClose, error, isNotFound }: ErrorComponentProps) => {
    const styles = useStyles();
    const i18n = useI18n();
    const isLarge = useIsScreenSizeMin("large");
    const { presentMessageComposer } = useIntercom();

    const onAssistancePress = useCallback(() => {
        void presentMessageComposer();
    }, [presentMessageComposer]);

    useEffect(() => {
        if (!isNotFound) {
            console.warn(error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <ScreenTitle style={styles.title}>
                {i18n.t(`screens.error_screen.${isNotFound ? "not_found" : "error"}_title`)}
            </ScreenTitle>
            <ScreenCard
                buttons={[
                    {
                        title: i18n.t("retry"),
                        onPress: onClose,
                        testID: "retry",
                        mode: "outlined",
                    },
                    {
                        title: i18n.t("assistance"),
                        onPress: onAssistancePress,
                        testID: "assistance",
                        mode: "secondary",
                    },
                ]}
                style={styles.screenCard}
                centerContent
                {...(isLarge
                    ? {
                          contentContainerStyle: {
                              alignItems: "center",
                          },
                      }
                    : {})}
            >
                <LicorneColourSvg width="100%" />
                <Gap gap={1} />
                <MarkdownView
                    markdown={i18n.t(
                        `screens.error_screen.${isNotFound ? "not_found" : "error"}_description`,
                    )}
                />
                <Text style={styles.error}>{error.message}</Text>
            </ScreenCard>
        </>
    );
};

const useStyles = () => {
    const { gridSize, colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                title: {
                    ...fonts.titleSmall,
                    alignSelf: "center",
                    marginTop: gridSize / 1.5,
                    marginBottom: gridSize,
                },
                screenCard: {
                    paddingHorizontal: gridSize / 1.5,
                },
                error: {
                    ...fonts.bodySmall,
                    color: colors.fieldLabel,
                    marginTop: gridSize * 2,
                },
            }),
        [colors.fieldLabel, fonts.bodySmall, fonts.titleSmall, gridSize],
    );
};
